import { useEffect, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import {
  getPlayers,
  TopScorersResponse,
  getOwnGoals,
  OwnGoalEntry,
} from "../../Services/home";
import { RootState } from "../../Store/store";

export const useHome = () => {
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState<TopScorersResponse>();
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [ownGoals, setOwnGoals] = useState<OwnGoalEntry[] | undefined>(
    undefined
  );
  const [ownGoalsRequested, setOwnGoalsRequested] = useState(false);
  const [ownGoalsLoading, setOwnGoalsLoading] = useState(false);
  const [ownGoalsError, setOwnGoalsError] = useState<string | null>(null);
  const [ownGoalsRetry, setOwnGoalsRetry] = useState(0);
  const ownGoalsRequests = useRef(new Map<number, ReturnType<typeof getOwnGoals>>());

  const { season } = useSelector((state: RootState) => state);

  const toast = useToast();

  useEffect(() => {
    const seasonId = season?.data?.id;
    if (!seasonId) return;

    const fetchPlayers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPlayers(seasonId);
        setPlayers(response);
        setRetryCount(0);
      } catch (err) {
        if (retryCount < 1) {
          setRetryCount(retryCount + 1);
          fetchPlayers();
        } else {
          setError("Ocorreu um erro ao buscar os jogadores.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers();
  }, [season?.data?.id, retryCount]);

  useEffect(() => {
    const seasonId = season?.data?.id;
    setOwnGoals(undefined);
    setOwnGoalsError(null);
    if (!ownGoalsRequested || !seasonId) return;

    let active = true;
    setOwnGoalsLoading(true);
    let request = ownGoalsRequests.current.get(seasonId);
    if (!request) {
      request = getOwnGoals(seasonId);
      ownGoalsRequests.current.set(seasonId, request);
    }

    request.then((response) => {
      if (!response) throw new Error("Resposta indisponível");
      if (active) setOwnGoals(response);
    }).catch(() => {
      ownGoalsRequests.current.delete(seasonId);
      if (active) setOwnGoalsError("Não foi possível carregar os gols contra.");
    }).finally(() => {
      if (active) setOwnGoalsLoading(false);
    });

    return () => { active = false; };
  }, [season?.data?.id, ownGoalsRequested, ownGoalsRetry]);

  useEffect(() => {
    if (error) {
      toast({
        title: "Erro",
        description: error,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [error, toast]);

  return {
    loading,
    players,
    ownGoals,
    ownGoalsLoading,
    ownGoalsError,
    onTabChange: (index: number) => {
      if (index === 1) setOwnGoalsRequested(true);
    },
    retryOwnGoals: () => setOwnGoalsRetry((count) => count + 1),
    error,
  };
};
